import { z } from 'zod';

export const documentKindSchema = z.enum(['book', 'article']);
export type DocumentKind = z.infer<typeof documentKindSchema>;

/**
 * Documents are addressed by source and that source's own id, so a link to one stays valid
 * whether or not it has been imported yet:
 * - `gutenberg-1342`: a Project Gutenberg book;
 * - `pmc-13628228`: a Europe PMC article;
 * - `archive-greatgatsby0000fsco`: the recognised text of a free Internet Archive scan;
 * - `wikisource-Q14943162`: a Wikisource edition, by its Wikidata item;
 * - `standardebooks-mary-shelley_frankenstein`: a Standard Ebooks book, by its source repository;
 * - `oapen-101963`: an open-access book in the OAPEN Library, by its handle;
 * - `added-42`: a book a reader added to Libellus by link.
 */
export const DOCUMENT_ID_PATTERN =
  /^(gutenberg-[1-9]\d{0,7}|pmc-[1-9]\d{0,9}|archive-[A-Za-z0-9][A-Za-z0-9._-]{0,99}|wikisource-Q[1-9]\d{0,10}|standardebooks-[a-z0-9][a-z0-9_-]{0,99}|oapen-[1-9]\d{0,9}|added-[1-9]\d{0,14})$/;
export const documentIdSchema = z.string().regex(DOCUMENT_ID_PATTERN, 'Unknown document id');
export const documentParamsSchema = z.object({ documentId: documentIdSchema });

export const blockKindSchema = z.enum(['heading', 'paragraph', 'quote', 'verse']);
export type BlockKind = z.infer<typeof blockKindSchema>;

/** Text only: the reader renders blocks as plain text, never as source HTML. */
export const blockSchema = z.object({
  kind: blockKindSchema,
  text: z.string(),
});
export type Block = z.infer<typeof blockSchema>;

export const tocEntrySchema = z.object({
  index: z.number().int().min(0),
  title: z.string(),
  wordCount: z.number().int().min(0),
  blockCount: z.number().int().min(0),
});
export type TocEntry = z.infer<typeof tocEntrySchema>;

export const documentSummarySchema = z.object({
  id: documentIdSchema,
  kind: documentKindSchema,
  title: z.string(),
  authors: z.array(z.string()),
  year: z.number().int().nullable(),
  coverUrl: z.string().nullable(),
  /** Journal for articles, publisher line or first subject for books. */
  source: z.string().nullable(),
  /** Which edition this text is, when it matters: "Standard Ebooks", "Wikisource · Holt text". */
  edition: z.string().nullable().optional(),
  /** The publication year is still being looked up; it may appear shortly. */
  yearPending: z.boolean().optional(),
});
export type DocumentSummary = z.infer<typeof documentSummarySchema>;

export const documentDetailSchema = documentSummarySchema.extend({
  summary: z.string().nullable(),
  subjects: z.array(z.string()),
  sourceUrl: z.string(),
  wordCount: z.number().int().min(0),
  toc: z.array(tocEntrySchema),
});
export type DocumentDetail = z.infer<typeof documentDetailSchema>;

export const sectionParamsSchema = documentParamsSchema.extend({
  index: z.coerce.number().int().min(0),
});

export const sectionContentSchema = z.object({
  index: z.number().int().min(0),
  title: z.string(),
  blocks: z.array(blockSchema),
});
export type SectionContent = z.infer<typeof sectionContentSchema>;
